/**
 * Modal.tsx
 *
 * Satisfies two APIs:
 *  (a) molekule Modal — props: open, onClose, title, showClose, closeOnBackdropClick,
 *      minWidth, maxWidth, animationIn, animationOut, animationDuration
 *      Sub-components: Modal.Header, Modal.Body, Modal.Footer (used in ModalDelete.js)
 *
 *  (b) ui-core compound pattern — props: show, onClose, size, style, id
 *      Named exports: ModalHeader, ModalContent, ModalFooter
 *      Used in: StatusChangeModal, ModalForm, ContactList, Contacts/edit,
 *               Projects/RenewalModal (commented), Admin/Users/UserList,
 *               Account/GoogleContactsModal, Reports/index
 *
 * Built on @radix-ui/react-dialog. Styled with styled-components v5 (v6-compatible).
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import styled, { css, keyframes } from 'styled-components';
import { visuallyHidden } from './visuallyHidden';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const zoomIn = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1); }
`;

const zoomOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to   { opacity: 0; transform: scale(0.85); }
`;

// ---------------------------------------------------------------------------
// Context — lets ModalHeader close button call onClose
// ---------------------------------------------------------------------------

interface ModalCtx {
  handleClose: () => void;
  // Lets a header report its visible title text so the dialog's hidden
  // Dialog.Title (the accessible name) can echo it.
  setTitle: (title: string) => void;
}

const ModalContext = createContext<ModalCtx>({
  handleClose: () => {},
  setTitle: () => {},
});

// ---------------------------------------------------------------------------
// Styled primitives
// ---------------------------------------------------------------------------

const StyledOverlay = styled(DialogPrimitive.Overlay)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 30px 1rem 1rem;

  &[data-state='open'] {
    animation: ${fadeIn} 175ms ease-out forwards;
  }
  &[data-state='closed'] {
    animation: ${fadeOut} 175ms ease-in forwards;
  }
`;

interface StyledContentWrapProps {
  $minWidth?: number;
  $maxWidth?: number;
}

const StyledContentWrap = styled(
  DialogPrimitive.Content,
)<StyledContentWrapProps>`
  position: relative;
  margin: auto;
  min-width: ${(p) => (p.$minWidth != null ? p.$minWidth : 350)}px;
  max-width: ${(p) => (p.$maxWidth != null ? p.$maxWidth : 750)}px;
  width: 100%;
  background: #ffffff;
  background-clip: padding-box;
  box-shadow: 0px 0px 16px rgba(44, 53, 71, 0.2);
  border-radius: 8px;
  max-height: 90vh;
  outline: none;
  color: #121212;

  /* Column layout so the header/footer stay put and only the body scrolls
     when a form is taller than the 90vh cap. */
  display: flex;
  flex-direction: column;

  /* Size variants (ui-core size prop) */
  ${(p: any) =>
    p.$size === 'sm' &&
    css`
      max-width: 400px;
    `}
  ${(p: any) =>
    p.$size === 'md' &&
    css`
      max-width: 600px;
    `}
  ${(p: any) =>
    p.$size === 'lg' &&
    css`
      max-width: 900px;
    `}

  &[data-state='open'] {
    animation: ${zoomIn} 175ms cubic-bezier(0.25, 0.1, 0.17, 1.2) forwards;
  }
  &[data-state='closed'] {
    animation: ${zoomOut} 175ms cubic-bezier(0.25, 0.1, 0.17, 1.2) forwards;
  }
`;

// Every dialog needs an accessible name. Radix wires Dialog.Content's
// aria-labelledby to this Title automatically; it stays visually hidden
// because both header APIs render their own visible title/children.
const VisuallyHiddenTitle = styled(DialogPrimitive.Title)`
  ${visuallyHidden}
`;

// ---------------------------------------------------------------------------
// Modal.Header (molekule API) — used in ModalDelete.js
// ---------------------------------------------------------------------------

const StyledMolHeader = styled.div`
  padding: 1rem 1.25rem 0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  flex: 0 0 auto;
`;

const StyledMolHeaderInner = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #d1d2d5;
`;

const StyledMolTitle = styled.h2`
  font-size: 16px;
  margin: 0;
  outline: none;
  flex: 1;
`;

interface MolHeaderProps {
  title?: React.ReactNode;
  showClose?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

function MoleculeHeader({
  title,
  showClose = true,
  children,
  style,
  className,
}: MolHeaderProps) {
  const { handleClose, setTitle } = useContext(ModalContext);

  // Report a plain-text title as the dialog's accessible name (covers
  // standalone <Modal.Header title="..."> usage without a Modal title prop).
  useEffect(() => {
    if (typeof title === 'string') setTitle(title);
  }, [title, setTitle]);

  return (
    <StyledMolHeader style={style} className={className}>
      <StyledMolHeaderInner>
        {title && <StyledMolTitle>{title}</StyledMolTitle>}
        {children}
        {showClose && (
          <button
            type="button"
            aria-label="Close Modal"
            onClick={handleClose}
            style={{
              marginLeft: 'auto',
              marginRight: -6,
              outline: 'none',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: 4,
            }}
          >
            {/* Simple X icon */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#494D55"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </StyledMolHeaderInner>
    </StyledMolHeader>
  );
}

// ---------------------------------------------------------------------------
// Modal.Body (molekule API) — used in ModalDelete.js
// ---------------------------------------------------------------------------

// Sized by the flex column rather than a hardcoded `calc(90vh - 105px)`, so the
// body yields exactly the space the header and footer actually take.
const MoleculeBody = styled.div`
  padding: 1.25rem;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

// ---------------------------------------------------------------------------
// Modal.Footer (molekule API) — used in ModalDelete.js
// ---------------------------------------------------------------------------

const MoleculeFooter = styled.div`
  padding: 0 1.25rem 1rem;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  flex: 0 0 auto;
`;

// ---------------------------------------------------------------------------
// ui-core compound sub-components
// ---------------------------------------------------------------------------

// ModalHeader (ui-core) — has a close button, forwarded from context
const UICoreHeaderWrapper = styled.div`
  padding: 16px 24px;
  position: relative;
  border-bottom: 1px solid #e8e9ea;
  border-radius: 4px 4px 0 0;
  min-height: 55px;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 35px;
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
`;

interface ModalHeaderProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
}

export function ModalHeader({
  children,
  style,
  className,
  ...rest
}: ModalHeaderProps) {
  const { handleClose, setTitle } = useContext(ModalContext);

  // Report plain-text children as the dialog's accessible name.
  useEffect(() => {
    if (typeof children === 'string') setTitle(children);
  }, [children, setTitle]);

  return (
    <UICoreHeaderWrapper
      data-testid="modal__header"
      style={style}
      className={className}
      {...rest}
    >
      {children}
      <CloseButton
        type="button"
        onClick={handleClose}
        data-testid="modal__close-button"
        aria-label="Close"
      >
        {/* FiX equivalent */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </CloseButton>
    </UICoreHeaderWrapper>
  );
}

// ModalContent (ui-core)
// Owns the scroll for tall content. `min-height: 0` is required — without it a
// flex item refuses to shrink below its content size and the overflow never
// engages, leaving the bottom of the content unreachable.
const UICoreContentWrapper = styled.div`
  padding: 20px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

interface ModalContentProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
}

export function ModalContent({
  children,
  style,
  className,
  ...rest
}: ModalContentProps) {
  return (
    <UICoreContentWrapper
      data-testid="modal__content"
      style={style}
      className={className}
      {...rest}
    >
      {children}
    </UICoreContentWrapper>
  );
}

// ModalFooter (ui-core)
const UICoreFooterWrapper = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e8e9ea;
  border-radius: 0 0 4px 4px;
  flex: 0 0 auto;
`;

interface ModalFooterProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
}

export function ModalFooter({
  children,
  style,
  className,
  ...rest
}: ModalFooterProps) {
  return (
    <UICoreFooterWrapper
      data-testid="modal__footer"
      style={style}
      className={className}
      {...rest}
    >
      {children}
    </UICoreFooterWrapper>
  );
}

// ---------------------------------------------------------------------------
// Main Modal component
// Supports both APIs:
//   molekule: open, onClose, title, showClose, closeOnBackdropClick, minWidth, maxWidth
//   ui-core:  show, onClose, size, id, style
// ---------------------------------------------------------------------------

interface ModalProps {
  // molekule props
  open?: boolean;
  showClose?: boolean;
  closeOnBackdropClick?: boolean;
  title?: string;
  minWidth?: number;
  maxWidth?: number;
  animationIn?: string;
  animationOut?: string;
  animationDuration?: number;
  // ui-core props
  show?: boolean;
  size?: 'sm' | 'md' | 'lg';
  // shared
  onClose?: (info?: any) => void;
  children?: React.ReactNode;
  id?: string;
  style?: React.CSSProperties;
  className?: string;
  // forward unknown props without crashing
  [key: string]: any;
}

export function Modal({
  open,
  show,
  onClose,
  title,
  showClose = true,
  closeOnBackdropClick = true,
  minWidth,
  maxWidth,
  size,
  id,
  style,
  className,
  children,
  // absorb unused molekule props
  animationIn: _animationIn,
  animationOut: _animationOut,
  animationDuration: _animationDuration,
  // absorb ui-core props
  backdropProps: _backdropProps,
  disableAutoFocus: _daf,
  disableFocusOn: _dfo,
  disableOnClickOutside: _doco,
  disableOnEscapeKey: _doek,
  disableReturnFocus: _drf,
  focusOnProps: _fop,
  transitionProps: _tp,
  variant: _variant,
  onEscapeKey: _oek,
  onClickOutside: _oco,
  ...rest
}: ModalProps) {
  // Support both `open` (molekule) and `show` (ui-core)
  const isOpen = open ?? show ?? false;

  // Title text reported by a ui-core ModalHeader (used when no `title` prop).
  const [headerTitle, setHeaderTitle] = useState('');

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleOverlayClick = () => {
    if (closeOnBackdropClick) handleClose();
  };

  return (
    <ModalContext.Provider value={{ handleClose, setTitle: setHeaderTitle }}>
      <DialogPrimitive.Root
        open={isOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleClose();
        }}
        modal
      >
        <DialogPrimitive.Portal>
          <StyledOverlay
            data-testid="modal__backdrop"
            onClick={handleOverlayClick}
          >
            <StyledContentWrap
              data-testid="modal"
              id={id}
              style={style}
              className={className}
              $minWidth={minWidth}
              $maxWidth={maxWidth}
              $size={size}
              onClick={handleContentClick}
              aria-modal="true"
              aria-describedby={undefined}
              onPointerDownOutside={(e) => {
                // Prevent Radix's own outside-click close so our overlay handler controls it
                e.preventDefault();
              }}
              {...(rest as any)}
            >
              <VisuallyHiddenTitle>
                {title || headerTitle || 'Modal dialog'}
              </VisuallyHiddenTitle>
              {/* molekule title-prop shorthand */}
              {title && <MoleculeHeader title={title} showClose={showClose} />}
              {children}
            </StyledContentWrap>
          </StyledOverlay>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </ModalContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Attach molekule sub-components as static props on Modal
// ---------------------------------------------------------------------------

Modal.Header = MoleculeHeader;
Modal.Body = MoleculeBody;
Modal.Footer = MoleculeFooter;

export default Modal;
